import { toast } from "react-toastify";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FullScreenLoader, PageTitle } from "components";
import { useGetACompanyQuery, useGetAllCompaniesQuery } from "redux/api/companyApi";
import { ISelectOption } from "Interfaces/Utils";
import { ICompany } from "Models/Company";
import { useGetAPieceOfOperationQuery, usePutOperationMutation } from "redux/api/operationApi";
import { useGetAllPointOfSaleQuery } from "redux/api/pointOfSaleApi";
import { useGetAllDocumentTypesQuery } from "redux/api/documentTypeApi";
import { IPointOfSale } from "Models/PointOfSale";
import { Operation } from "components/Operations";
import { skipToken } from "@reduxjs/toolkit/dist/query/react";
import { useIntl } from "react-intl";
import { useGetAllProfesionalActivitiesQuery } from "redux/api/profesionalActivtyApi";

// Static vars

const RETURN_PAGE = "/operations";

/**
 * Component to render user edition view
*/

const OperationEditPage = () => {
  const { formatMessage } = useIntl();
  const PAGE_TITLE = formatMessage({ id: "operation.editTitle" });
  const RETURN_LABEL = formatMessage({ id: "operation.returnLabel" });
  const { operationId } = useParams();
  const UPDATE_SUCCESS_LABEL = formatMessage({ id: "operation.updatedSuccessfuly" });
  const navigate = useNavigate();

  // API calls

  const { data: operation, isLoading: isOperationLoading } = useGetAPieceOfOperationQuery(String(operationId));
  const { data: company, isLoading: isCompanyLoading } = useGetACompanyQuery(operation?.companyId ? String(operation.companyId) : skipToken);
  const [updateOperation, { isSuccess: isUpdatedOperationSuccess, error: apiError }] = usePutOperationMutation();
  const {data: pointsOfSale, isLoading: isPointsOfSaleLoading} = useGetAllPointOfSaleQuery();
  const {data: documentType, isLoading: isDocumentTypeLoading} = useGetAllDocumentTypesQuery();
  const {
    data: activities,
    isLoading:isActivitiesLoading,
    error: apiActivitiesError,
  } = useGetAllProfesionalActivitiesQuery();
  // UseEffect to manage creation success

  useEffect(() => {
    if (isUpdatedOperationSuccess) {
      toast.success(UPDATE_SUCCESS_LABEL);
      navigate(RETURN_PAGE);
    }
  }, [isUpdatedOperationSuccess, navigate]);


  // Function to manage form submit

  const updateOperationHandler = (formData: FormData) => {    
    updateOperation({ id: String(operationId), operationToUpdate: formData });
  };
  const CompanySelectOptions: ISelectOption[] = useMemo(() => {

    if (!company) return [];
    
    return [{
      label: company.name,
      value: company.id,
    }];
    
  }, [company])
  const pointOfSaleSelectOptions: ISelectOption[] = useMemo(() => {

    if (!pointsOfSale) return [];

    return pointsOfSale.map((pointOfSale: IPointOfSale) => ({
      label: pointOfSale.name,
      value: pointOfSale.id,
    }));
    
  }, [pointsOfSale])

  const documentTypeSelectOptions: ISelectOption[] = useMemo(() => {
        
    if (!documentType) return [];

    return documentType.map((documentType) => ({
      label: documentType.name,
      value: documentType._id,
    }));
    
  }, [documentType])

  const profesionalActivitySelectOptions: ISelectOption[] = useMemo(() => {
        
    if (!activities) return [];

    return activities.map((activity) => ({
      label: activity.name,
      value: activity._id,
    }));
    
  }, [activities])


  if (isOperationLoading||isCompanyLoading||isPointsOfSaleLoading||isDocumentTypeLoading||isActivitiesLoading) return <FullScreenLoader />;

  return (
    <>
      <PageTitle
        pageTitle={PAGE_TITLE}
        returnPage={RETURN_PAGE}
        returnLabel={RETURN_LABEL}
      />
      <Operation.Form
        companies={CompanySelectOptions}
        pointsOfSale={pointOfSaleSelectOptions} 
        documentType={documentTypeSelectOptions}
        currentOperation={operation}
        profesionalActivities={profesionalActivitySelectOptions}
        apiError={apiError}
        onSubmit={updateOperationHandler}
      />
    </>
  );
};

export default OperationEditPage;